import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, Ticket, Users, Video } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-secondary/20"></div>
        
        {/* Hero image */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1751922001569-a1cebe339e34?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* Logo */}
          <div className="mb-8 animate-fade-in">
            <img 
              src="https://customer-assets.emergentagent.com/job_3df344ce-b978-4374-b0b0-7e9d1746e70f/artifacts/w7gh4arh_vibezlink_logo_transparent.png" 
              alt="Vibezlink Logo" 
              className="h-32 md:h-48 mx-auto drop-shadow-2xl"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter mb-6 animate-fade-in">
            <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              VIBEZLINK
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 animate-slide-up">
            More Than A Ticket: A Social Experience
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
            <Button 
              size="lg" 
              className="bg-white text-black hover:bg-gray-200 font-bold rounded-full px-8 py-6 text-lg shadow-glow"
              onClick={() => navigate('/register')}
            >
              Get Started
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="glass-hover border-white/20 rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/discover')}
            >
              Browse Events
            </Button>
            <Button 
              size="lg" 
              variant="ghost" 
              className="text-gray-400 hover:text-white rounded-full px-8 py-6 text-lg"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-center mb-16">
            Everything You Need for Epic Events
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={<Ticket className="w-12 h-12 text-primary" />}
              title="Smart Ticketing"
              description="Secure QR code tickets with instant access to events"
            />
            <FeatureCard
              icon={<Sparkles className="w-12 h-12 text-secondary" />}
              title="Vibe Matching"
              description="AI-powered connections with like-minded attendees"
            />
            <FeatureCard
              icon={<Users className="w-12 h-12 text-accent-cyan" />}
              title="Social Hub"
              description="Chat, share memories, and make friends at events"
            />
            <FeatureCard
              icon={<Video className="w-12 h-12 text-accent-lime" />}
              title="Live Streaming"
              description="Virtual meetups and live event streams"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto glass rounded-3xl p-12 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-6">
            Ready to Experience Events Like Never Before?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of event-goers connecting, sharing, and vibing together.
          </p>
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 font-bold rounded-full px-12 py-6 text-lg shadow-glow"
            onClick={() => navigate('/register')}
          >
            Join Vibezlink Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center text-muted-foreground">
          <p>&copy; 2025 Vibezlink. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="glass rounded-2xl p-6 hover:border-white/20 transition-all hover:-translate-y-1">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-heading font-bold mb-2">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </div>
  );
}
